import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Divider,
  Paper,
  Checkbox,
  FormGroup,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CameraIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  WifiIcon,
  ClockIcon,
  PauseCircleIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { BASE_URL } from '../../App';
import Swal from 'sweetalert2';
import { decrypt } from '../../utils/crypto.js';
import { pdf } from '@react-pdf/renderer';
import MaintenanceReportPdf from './MaintenanceReportPdf';

export const OFFLINE_QUEUE_KEY = 'dimei_maint_offline_queue';

// --- Custom Signature Canvas Component ---
export const SignaturePad = ({ onSave, title, onCancel }) => {
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const [history, setHistory] = useState([]);

  const initContext = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a';
    return ctx;
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches && e.touches.length > 0 ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches && e.touches.length > 0 ? e.touches[0].clientY : e.clientY;
    const scaleX = canvas.width / (rect.width || 1);
    const scaleY = canvas.height / (rect.height || 1);
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = initContext();
    if (!ctx) return;

    try {
      const currentImgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      setHistory((prev) => [...prev, currentImgData]);
    } catch (err) {}

    isDrawingRef.current = true;
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    ctx.lineTo(pos.x + 0.1, pos.y + 0.1);
    ctx.stroke();

    if (e.cancelable && e.touches) e.preventDefault();
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();

    if (e.cancelable && e.touches) e.preventDefault();
  };

  const stopDrawing = () => {
    if (isDrawingRef.current) {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.closePath();
      }
      isDrawingRef.current = false;
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    initContext();

    const handleTouchStart = (e) => startDrawing(e);
    const handleTouchMove = (e) => draw(e);
    const handleTouchEnd = () => stopDrawing();

    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd);
    canvas.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('touchcancel', handleTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHistory([]);
  };

  const undo = () => {
    if (history.length === 0) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const lastState = history[history.length - 1];
    ctx.putImageData(lastState, 0, 0);
    setHistory((prev) => prev.slice(0, -1));
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    const blank = document.createElement('canvas');
    blank.width = canvas.width;
    blank.height = canvas.height;
    if (canvas.toDataURL() === blank.toDataURL()) {
      Swal.fire('Error', 'Por favor, dibuja la firma primero.', 'warning');
      return;
    }
    onSave(canvas.toDataURL());
  };

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="subtitle1" fontWeight="800" gutterBottom>
        {title}
      </Typography>
      <Box
        sx={{
          border: '2px dashed #cbd5e1',
          borderRadius: 4,
          overflow: 'hidden',
          bgcolor: '#fff',
          touchAction: 'none',
        }}
      >
        <canvas
          ref={canvasRef}
          width={500}
          height={200}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          style={{
            display: 'block',
            cursor: 'crosshair',
            width: '100%',
            height: '200px',
            touchAction: 'none',
          }}
        />
      </Box>
      <Grid container spacing={1} sx={{ mt: 2 }}>
        {onCancel && (
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="text"
              onClick={onCancel}
              sx={{ textTransform: 'none', color: '#64748b', fontWeight: '700', mb: 1 }}
            >
              Volver a firmas guardadas
            </Button>
          </Grid>
        )}
        <Grid item xs={4}>
          <Button
            fullWidth
            variant="outlined"
            color="warning"
            onClick={undo}
            disabled={history.length === 0}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Deshacer
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button
            fullWidth
            variant="outlined"
            color="error"
            onClick={clear}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Borrar
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSave}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Confirmar Firma
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default function FormularioMantenimiento() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tipoParam = searchParams.get('tipo') || 'Preventivo';
  const reportIdParam = searchParams.get('reportId') || null;

  const company = sessionStorage.getItem('company') || 1;
  const userId = decrypt(sessionStorage.getItem('userId'));
  const userName = decrypt(sessionStorage.getItem('name'));
  const backendUrl = BASE_URL.replace('/api/v1/', '');

  const draftStorageKey = `dimei_maint_draft_${id}_${reportIdParam || 'new'}`;

  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [maintenanceType, setMaintenanceType] = useState(tipoParam);
  const [recommendations, setRecommendations] = useState([]);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [availableActivities, setAvailableActivities] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]); // [{ id, name, completed, observation }]
  const [customPreActivity, setCustomPreActivity] = useState('');
  // Las actividades son acciones previas antes del mantenimiento
  const [preActivitiesCompleted, setPreActivitiesCompleted] = useState(Boolean(reportIdParam));

  const [techniciansList, setTechniciansList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [equipo, setEquipo] = useState(null);
  const [questionGroup, setQuestionGroup] = useState(null);
  const [answers, setAnswers] = useState({});
  const [generalObservations, setGeneralObservations] = useState('');

  // Tiempos de inicio y por pregunta (solo visible para Admin en historial)
  const [startTime, setStartTime] = useState(() => new Date().toISOString());
  const [questionTimings, setQuestionTimings] = useState({}); // { [questionId]: { duration_seconds, answered_at } }

  // Navegación pregunta por pregunta con flechas
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Estado cuando se continúa un reporte parcial
  const [existingReport, setExistingReport] = useState(null);

  // --- Signature & Closing Flow State ---
  // closeMode: 'final_deliver' (3 firmas) | 'pending_delivery' (solo firma quien ejecutó) | 'partial' (cierre parcial + enlazar técnico)
  const [closeMode, setCloseMode] = useState('final_deliver');
  const [showSignatureFlow, setShowSignatureFlow] = useState(false);
  // signatureStep: 'executor' | 'delivery' | 'customer'
  const [signatureStep, setSignatureStep] = useState('executor');
  const [savedSignatures, setSavedSignatures] = useState([]);
  const [technicianSignature, setTechnicianSignature] = useState(null);
  const [deliveryTechnicianSignature, setDeliveryTechnicianSignature] = useState(null);
  const [isDrawingNewTech, setIsDrawingNewTech] = useState(false);
  const [customerName, setCustomerName] = useState('');

  // Cierre parcial state
  const [continuedByTechnicianId, setContinuedByTechnicianId] = useState('');
  const [partialCloseObservations, setPartialCloseObservations] = useState('');

  // Online / Offline listeners
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('data:') || path.startsWith('blob:')) return path;
    const base = BASE_URL.endsWith('/') ? BASE_URL : BASE_URL + '/';
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    const token = sessionStorage.getItem('Token') || '';
    return `${base}${cleanPath}?token=${encodeURIComponent(token)}`;
  };

  // Lista de pasos (1 pregunta a la vez + paso final de resumen/cierre)
  const isSpecialType = maintenanceType === 'Emergencia' || maintenanceType === 'Correctivo';
  const specialSteps = useMemo(
    () => [
      { id: 9001, title: '1. DESCRIPCIÓN DE NOVEDAD *', type: 'special_novedad' },
      { id: 9002, title: '2. PROCEDIMIENTO REALIZADO *', type: 'special_procedimiento' },
      { id: 9003, title: '3. ¿LA FALLA FUE SUBSANADA? *', type: 'special_subsanada' },
    ],
    []
  );

  const activeQuestionsList = useMemo(() => {
    if (isSpecialType) return specialSteps;
    return questionGroup?.questions || [];
  }, [isSpecialType, questionGroup, specialSteps]);

  const filteredActivities = useMemo(
    () =>
      availableActivities.filter(
        (a) =>
          (!a.maintenance_type ||
            a.maintenance_type === 'Todos' ||
            a.maintenance_type === maintenanceType) &&
          (!a.elevator_type_id ||
            !equipo?.elevatorType ||
            Number(a.elevator_type_id) === Number(equipo?.elevatorType))
      ),
    [availableActivities, maintenanceType, equipo]
  );

  // El último paso (índice === activeQuestionsList.length) es la sección de Repuestos, Observaciones y Cierre
  const totalSteps = activeQuestionsList.length + 1;
  const isFinalSummaryStep = currentStepIndex >= activeQuestionsList.length;
  const currentQuestion = !isFinalSummaryStep ? activeQuestionsList[currentStepIndex] : null;

  // Cronómetro silencioso por pregunta (solo corre cuando ya pasó las actividades previas y está en una pregunta)
  useEffect(() => {
    if (loading || !preActivitiesCompleted || isFinalSummaryStep || !currentQuestion?.id) {
      return undefined;
    }
    const qId = currentQuestion.id;
    const interval = setInterval(() => {
      setQuestionTimings((prev) => {
        const current = prev[qId] || { duration_seconds: 0, answered_at: null };
        return {
          ...prev,
          [qId]: {
            ...current,
            duration_seconds: (current.duration_seconds || 0) + 1,
          },
        };
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [loading, preActivitiesCompleted, isFinalSummaryStep, currentQuestion?.id]);

  // Persistir borrador automáticamente en localStorage (Soporte Offline)
  useEffect(() => {
    if (loading) return;
    try {
      const draftPayload = {
        equipoId: id,
        reportId: reportIdParam,
        maintenanceType,
        answers,
        questionTimings,
        startTime,
        recommendations,
        selectedActivities,
        preActivitiesCompleted,
        generalObservations,
        currentStepIndex,
        continuedByTechnicianId,
        partialCloseObservations,
        updatedAt: new Date().toISOString(),
      };
      localStorage.setItem(draftStorageKey, JSON.stringify(draftPayload));
    } catch (e) {
      console.warn('No se pudo guardar borrador en localStorage:', e);
    }
  }, [
    loading,
    id,
    reportIdParam,
    maintenanceType,
    answers,
    questionTimings,
    startTime,
    recommendations,
    selectedActivities,
    preActivitiesCompleted,
    generalObservations,
    currentStepIndex,
    continuedByTechnicianId,
    partialCloseObservations,
    draftStorageKey,
  ]);

  useEffect(() => {
    fetchData();
    fetchMySignatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, reportIdParam]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // 1. Cargar Equipo (online con fallback a caché localStorage)
      let equipoData = null;
      try {
        const resEquipo = await axios.get(`/getOneProject/${id}`);
        equipoData = resEquipo.data.data?.[0];
        if (equipoData) {
          localStorage.setItem(`dimei_cache_equipo_${id}`, JSON.stringify(equipoData));
        }
      } catch (err) {
        const cachedEq = localStorage.getItem(`dimei_cache_equipo_${id}`);
        if (cachedEq) equipoData = JSON.parse(cachedEq);
      }

      if (!equipoData) {
        Swal.fire('Error', 'Equipo no encontrado (verifica tu conexión)', 'error');
        navigate('/mantenimiento/clientes');
        return;
      }
      setEquipo(equipoData);

      // 2. Cargar Grupos de Preguntas (online con fallback a caché localStorage)
      let qGroupData = { groups: [], questions: [] };
      try {
        const resGroups = await axios.get(`/getProjectQuestionGroups/${id}`);
        qGroupData = resGroups.data.data || { groups: [], questions: [] };
        localStorage.setItem(`dimei_cache_qgroup_${id}`, JSON.stringify(qGroupData));
      } catch (err) {
        const cachedQg = localStorage.getItem(`dimei_cache_qgroup_${id}`);
        if (cachedQg) qGroupData = JSON.parse(cachedQg);
      }
      setQuestionGroup(qGroupData);

      // 3. Cargar Catálogo de Actividades Previas y Técnicos
      try {
        const [resAct, resUsers] = await Promise.all([
          axios.get(`/getMaintenanceActivities/${company}`),
          axios.get(`/getUser/${company}`),
        ]);
        const acts = resAct.data.data || [];
        setAvailableActivities(acts);
        localStorage.setItem(`dimei_cache_activities_${company}`, JSON.stringify(acts));

        const allUsers = resUsers.data.data || [];
        const techs = allUsers.filter(
          (u) =>
            u.state !== false &&
            u.state !== 0 &&
            (u.Rol?.name === 'Técnicos' ||
              u.Rol?.name === 'Jefe de Mantenimiento' ||
              u.Rol?.name === 'Administrador')
        );
        setTechniciansList(techs);
        localStorage.setItem(`dimei_cache_techs_${company}`, JSON.stringify(techs));
      } catch (err) {
        try {
          const cachedActs = localStorage.getItem(`dimei_cache_activities_${company}`);
          if (cachedActs) setAvailableActivities(JSON.parse(cachedActs));
          const cachedTechs = localStorage.getItem(`dimei_cache_techs_${company}`);
          if (cachedTechs) setTechniciansList(JSON.parse(cachedTechs));
        } catch (e) {}
      }

      // 4. Si viene reportIdParam, cargar el reporte parcial del servidor para continuarlo
      if (reportIdParam) {
        try {
          const resRep = await axios.get(`/getMaintenanceReport/${reportIdParam}`);
          const rep = resRep.data.data;
          if (rep) {
            setExistingReport(rep);
            setMaintenanceType(rep.maintenance_type || tipoParam);
            setPreActivitiesCompleted(true);
            if (rep.start_time) setStartTime(new Date(rep.start_time).toISOString());
            if (Array.isArray(rep.recommendations) && rep.recommendations.length > 0) {
              setRecommendations(rep.recommendations);
              setShowRecommendations(true);
            }
            if (Array.isArray(rep.activities)) {
              setSelectedActivities(rep.activities);
            }
            setGeneralObservations(rep.general_observations || '');
            setPartialCloseObservations(rep.partial_close_observations || '');
            if (rep.continued_by_technician_id) {
              setContinuedByTechnicianId(rep.continued_by_technician_id);
            }

            const restoredAnswers = {};
            const restoredTimings = {};
            for (const ans of rep.answers || []) {
              restoredAnswers[ans.question_id] = {
                question_id: ans.question_id,
                optionIds: (ans.selected_options || []).map(String),
                text: ans.answer_text || '',
                photos: (ans.photos || []).map((p) => ({
                  preview: typeof p === 'string' ? p : p.preview || p.path,
                })),
              };
              restoredTimings[ans.question_id] = {
                duration_seconds: ans.duration_seconds || 0,
                answered_at: ans.answered_at || null,
              };
            }
            setAnswers(restoredAnswers);
            setQuestionTimings(restoredTimings);

            const qList = qGroupData.questions || [];
            const firstUnansweredIdx = qList.findIndex((q) => !restoredAnswers[q.id]);
            if (firstUnansweredIdx !== -1) {
              setCurrentStepIndex(firstUnansweredIdx);
            }
          }
        } catch (err) {
          console.warn('No se pudo cargar reporte parcial del servidor, revisando borrador local:', err);
        }
      }

      // 5. Restaurar borrador de localStorage si existe
      const savedDraftRaw = localStorage.getItem(draftStorageKey);
      if (savedDraftRaw) {
        try {
          const draft = JSON.parse(savedDraftRaw);
          if (
            draft &&
            ((draft.answers && Object.keys(draft.answers).length > 0) ||
              (Array.isArray(draft.selectedActivities) && draft.selectedActivities.length > 0))
          ) {
            if (draft.answers) setAnswers((prev) => ({ ...prev, ...draft.answers }));
            if (draft.questionTimings) {
              setQuestionTimings((prev) => ({ ...prev, ...draft.questionTimings }));
            }
            if (draft.startTime) setStartTime(draft.startTime);
            if (draft.maintenanceType && !reportIdParam) setMaintenanceType(draft.maintenanceType);
            if (Array.isArray(draft.recommendations) && draft.recommendations.length > 0) {
              setRecommendations(draft.recommendations);
              setShowRecommendations(true);
            }
            if (Array.isArray(draft.selectedActivities) && draft.selectedActivities.length > 0) {
              setSelectedActivities(draft.selectedActivities);
            }
            if (typeof draft.preActivitiesCompleted === 'boolean') {
              setPreActivitiesCompleted(draft.preActivitiesCompleted);
            }
            if (draft.generalObservations) setGeneralObservations(draft.generalObservations);
            if (typeof draft.currentStepIndex === 'number') {
              setCurrentStepIndex(draft.currentStepIndex);
            }
          }
        } catch (e) {}
      }
    } catch (error) {
      console.error('Error fetching form data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMySignatures = async () => {
    try {
      const res = await axios.get('/getMySignatures');
      const sigs = res.data.data || [];
      setSavedSignatures(sigs);
      if (sigs.length === 0) setIsDrawingNewTech(true);
      localStorage.setItem(`dimei_cache_signatures_${userId}`, JSON.stringify(sigs));
    } catch (error) {
      try {
        const cachedSigs = localStorage.getItem(`dimei_cache_signatures_${userId}`);
        if (cachedSigs) {
          const sigs = JSON.parse(cachedSigs);
          setSavedSignatures(sigs);
          if (sigs.length === 0) setIsDrawingNewTech(true);
        }
      } catch (e) {}
    }
  };

  const addRecommendation = () => {
    setRecommendations((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        description: '',
        quantity: '1',
        priority: 'Media',
        observation: '',
      },
    ]);
  };

  const updateRecommendation = (idx, field, val) => {
    setRecommendations((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: val };
      return updated;
    });
  };

  const removeRecommendation = (idx) => {
    setRecommendations((prev) => {
      const filtered = prev.filter((_, i) => i !== idx);
      if (filtered.length === 0) {
        setShowRecommendations(false);
      }
      return filtered.map((item, i) => ({ ...item, id: i + 1 }));
    });
  };

  const toggleActivity = (act) => {
    setSelectedActivities((prev) => {
      const exists = prev.find((a) => String(a.id) === String(act.id));
      if (exists) {
        return prev.filter((a) => String(a.id) !== String(act.id));
      }
      return [
        ...prev,
        {
          id: act.id,
          name: act.name,
          completed: true,
          observation: '',
        },
      ];
    });
  };

  const handleSelectAllActivities = () => {
    const allIds = filteredActivities.map((a) => String(a.id));
    const currentSelectedIds = selectedActivities.map((s) => String(s.id));
    const allSelected = allIds.every((idVal) => currentSelectedIds.includes(idVal));

    if (allSelected) {
      setSelectedActivities((prev) =>
        prev.filter((s) => !allIds.includes(String(s.id)))
      );
    } else {
      setSelectedActivities((prev) => {
        const existingMap = new Map(prev.map((item) => [String(item.id), item]));
        filteredActivities.forEach((act) => {
          if (!existingMap.has(String(act.id))) {
            existingMap.set(String(act.id), {
              id: act.id,
              name: act.name,
              completed: true,
              observation: '',
            });
          }
        });
        return Array.from(existingMap.values());
      });
    }
  };

  const handleAddCustomPreActivity = () => {
    const trimmed = customPreActivity.trim();
    if (!trimmed) return;
    setSelectedActivities((prev) => [
      ...prev,
      {
        id: `custom-${Date.now()}`,
        name: trimmed,
        completed: true,
        observation: '',
      },
    ]);
    setCustomPreActivity('');
  };

  const updateActivityObservation = (actId, observation) => {
    setSelectedActivities((prev) =>
      prev.map((a) => (String(a.id) === String(actId) ? { ...a, observation } : a))
    );
  };

  const handleAnswerChange = (questionId, value, type) => {
    const nowIso = new Date().toISOString();
    setQuestionTimings((prev) => ({
      ...prev,
      [questionId]: {
        duration_seconds: prev[questionId]?.duration_seconds || 1,
        answered_at: nowIso,
      },
    }));

    setAnswers((prev) => {
      const current = prev[questionId] || {
        question_id: questionId,
        optionIds: [],
        text: '',
        photos: [],
      };
      if (type === 'unica') return { ...prev, [questionId]: { ...current, optionIds: [value] } };
      if (type === 'multiple') {
        const ids = [...current.optionIds];
        const idx = ids.indexOf(value);
        if (idx > -1) ids.splice(idx, 1);
        else ids.push(value);
        return { ...prev, [questionId]: { ...current, optionIds: ids } };
      }
      if (type === 'texto') return { ...prev, [questionId]: { ...current, text: value } };
      return prev;
    });
  };

  const handleFileChange = async (questionId, e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const nowIso = new Date().toISOString();
    setQuestionTimings((prev) => ({
      ...prev,
      [questionId]: {
        duration_seconds: prev[questionId]?.duration_seconds || 1,
        answered_at: nowIso,
      },
    }));

    const newPhotos = await Promise.all(
      files.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve({ file, preview: reader.result });
            };
            reader.readAsDataURL(file);
          })
      )
    );

    setAnswers((prev) => {
      const current = prev[questionId] || {
        question_id: questionId,
        optionIds: [],
        text: '',
        photos: [],
      };
      return {
        ...prev,
        [questionId]: { ...current, photos: [...(current.photos || []), ...newPhotos] },
      };
    });
  };

  const removePhoto = (questionId, index) => {
    setAnswers((prev) => {
      const current = prev[questionId];
      if (!current) return prev;
      const photos = [...current.photos];
      photos.splice(index, 1);
      return { ...prev, [questionId]: { ...current, photos } };
    });
  };

  useEffect(() => {
    if (maintenanceType === 'Emergencia' || maintenanceType === 'Correctivo') {
      setAnswers((prev) => {
        if (!prev[9003]?.optionIds?.length) {
          return {
            ...prev,
            9003: {
              question_id: 9003,
              optionIds: ['SI'],
              text: 'SI',
              photos: [],
            },
          };
        }
        return prev;
      });
    }
  }, [maintenanceType]);

  // Verifica si una pregunta específica está completa
  const isQuestionAnswered = (q) => {
    if (!q) return false;
    if (q.id === 9001 || q.id === 9002) {
      return Boolean((answers[q.id]?.text || '').trim());
    }
    if (q.id === 9003) {
      return Boolean(answers[9003]?.optionIds?.[0] || answers[9003]?.text);
    }
    const ans = answers[q.id];
    const photoCount = ans?.photos?.length || 0;
    if (q.type === 'unica' || q.type === 'multiple') {
      if (!ans?.optionIds || ans.optionIds.length === 0) return false;
    } else if (q.type === 'abierta') {
      if (!ans?.text || !ans.text.trim()) return false;
    }
    const selectedOptions =
      q.options?.filter((opt) => ans?.optionIds?.includes(opt.id.toString())) || [];
    const optionRequiresEvidence = selectedOptions.some((opt) => opt.requires_photo);
    const optionRequiresJustification = selectedOptions.some(
      (opt) => opt.requires_justification || opt.requires_justification === 1
    );
    if (optionRequiresJustification && (!ans?.text || !ans.text.trim())) {
      return false;
    }
    if (q.type === 'fotos' || optionRequiresEvidence) {
      const minRequired = q.min_photos > 0 ? q.min_photos : optionRequiresEvidence ? 1 : 0;
      if (photoCount < minRequired) return false;
      if (q.max_photos > 0 && photoCount > q.max_photos) return false;
    }
    return true;
  };

  const validateSingleQuestion = (q) => {
    if (!q) return true;
    if (q.id === 9001) {
      if (!(answers[9001]?.text || '').trim()) {
        Swal.fire('Atención', 'Por favor ingresa la descripción de la novedad.', 'warning');
        return false;
      }
      return true;
    }
    if (q.id === 9002) {
      if (!(answers[9002]?.text || '').trim()) {
        Swal.fire('Atención', 'Por favor ingresa el procedimiento realizado.', 'warning');
        return false;
      }
      return true;
    }
    if (q.id === 9003) {
      if (!(answers[9003]?.optionIds?.[0] || answers[9003]?.text)) {
        Swal.fire('Atención', 'Por favor indica si la falla fue subsanada (SI o NO).', 'warning');
        return false;
      }
      return true;
    }

    const ans = answers[q.id];
    const photoCount = ans?.photos?.length || 0;
    if (q.type === 'unica' || q.type === 'multiple') {
      if (!ans?.optionIds || ans.optionIds.length === 0) {
        Swal.fire('Atención', `La pregunta "${q.text}" no ha sido respondida.`, 'warning');
        return false;
      }
    } else if (q.type === 'abierta') {
      if (!ans?.text || !ans.text.trim()) {
        Swal.fire('Atención', `La pregunta "${q.text}" requiere una respuesta escrita.`, 'warning');
        return false;
      }
    }
    const selectedOptions =
      q.options?.filter((opt) => ans?.optionIds?.includes(opt.id.toString())) || [];
    const optionRequiresEvidence = selectedOptions.some((opt) => opt.requires_photo);
    const optionRequiresJustification = selectedOptions.some(
      (opt) => opt.requires_justification || opt.requires_justification === 1
    );
    if (optionRequiresJustification && (!ans?.text || !ans.text.trim())) {
      Swal.fire(
        'Atención',
        `La opción seleccionada en la pregunta "${q.text}" requiere justificación escrita.`,
        'warning'
      );
      return false;
    }
    if (q.type === 'fotos' || optionRequiresEvidence) {
      const minRequired = q.min_photos > 0 ? q.min_photos : optionRequiresEvidence ? 1 : 0;
      if (photoCount < minRequired) {
        Swal.fire(
          'Atención',
          `La pregunta "${q.text}" requiere al menos ${minRequired} foto(s) de evidencia.`,
          'warning'
        );
        return false;
      }
      if (q.max_photos > 0 && photoCount > q.max_photos) {
        Swal.fire(
          'Atención',
          `La pregunta "${q.text}" excede el máximo de ${q.max_photos} fotos permitidas.`,
          'warning'
        );
        return false;
      }
    }
    return true;
  };

  const validateFullForm = () => {
    for (let i = 0; i < activeQuestionsList.length; i++) {
      const q = activeQuestionsList[i];
      if (!validateSingleQuestion(q)) {
        setPreActivitiesCompleted(true);
        setCurrentStepIndex(i);
        return false;
      }
    }
    return true;
  };

  const handleNextStep = () => {
    if (!isFinalSummaryStep && currentQuestion) {
      if (!validateSingleQuestion(currentQuestion)) return;
    }
    if (currentStepIndex < totalSteps - 1) {
      setCurrentStepIndex((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Si está en la primera pregunta y da atrás, vuelve a las acciones previas
      setPreActivitiesCompleted(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const fetchImageAsBase64 = async (path) => {
    if (!path) return null;
    if (path.startsWith('data:') || path.startsWith('blob:')) return path;
    try {
      const url = getImageUrl(path);
      const response = await axios.get(url, { responseType: 'blob' });
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(response.data);
      });
    } catch (error) {
      return path;
    }
  };

  const handleOpenCloseFlow = (mode) => {
    // mode: 'final_deliver' | 'pending_delivery' | 'partial'
    if (mode !== 'partial' && !validateFullForm()) return;

    setCloseMode(mode);
    setTechnicianSignature(null);
    setDeliveryTechnicianSignature(null);
    setSignatureStep('executor');
    setIsDrawingNewTech(savedSignatures.length === 0);
    setShowSignatureFlow(true);
  };

  const handleSelectSavedSignature = async (sig) => {
    try {
      Swal.fire({
        title: 'Cargando firma...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
      const base64 = await fetchImageAsBase64(sig.signature);
      Swal.close();
      if (base64) {
        onTechSignatureConfirmed(base64);
      } else {
        Swal.fire('Error', 'No se pudo cargar la firma seleccionada.', 'error');
      }
    } catch (error) {
      Swal.close();
      Swal.fire('Error', 'No se pudo cargar la firma seleccionada.', 'error');
    }
  };

  const handleSaveTechSignature = async (base64) => {
    if (isOnline) {
      try {
        const res = await axios.post('/saveSignature', {
          user_id: userId,
          signature: base64,
          name: 'Firma Técnico',
        });
        setSavedSignatures([res.data.data]);
      } catch (e) {}
    }
    onTechSignatureConfirmed(base64);
  };

  const onTechSignatureConfirmed = (base64) => {
    if (signatureStep === 'executor') {
      setTechnicianSignature(base64);
      if (closeMode === 'partial') {
        handleExecuteSubmit({
          mode: 'partial',
          execSig: base64,
          delivSig: null,
          custSig: null,
        });
      } else if (closeMode === 'pending_delivery') {
        handleExecuteSubmit({
          mode: 'pending_delivery',
          execSig: base64,
          delivSig: null,
          custSig: null,
        });
      } else {
        // 'final_deliver' -> avanzar a firma de Quien Entrega
        setSignatureStep('delivery');
        setIsDrawingNewTech(false);
      }
    } else if (signatureStep === 'delivery') {
      setDeliveryTechnicianSignature(base64);
      setSignatureStep('customer');
    }
  };

  const saveToOfflineQueue = (submissionItem) => {
    try {
      const existingRaw = localStorage.getItem(OFFLINE_QUEUE_KEY);
      const queue = existingRaw ? JSON.parse(existingRaw) : [];
      queue.push({
        ...submissionItem,
        queuedAt: new Date().toISOString(),
        equipoName: equipo?.nombre || `${equipo?.elevatorTypeName || 'Equipo'} #${id}`,
      });
      localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
      localStorage.removeItem(draftStorageKey);
    } catch (e) {
      console.error('Error guardando en cola offline:', e);
    }
  };

  const handleExecuteSubmit = async ({ mode, execSig, delivSig, custSig }) => {
    if (mode === 'final_deliver' && !customerName.trim()) {
      Swal.fire(
        'Atención',
        'Por favor ingresa el nombre completo de quien recibe (cliente).',
        'warning'
      );
      return;
    }
    if (mode === 'partial' && !continuedByTechnicianId) {
      Swal.fire(
        'Atención',
        'Por favor selecciona el técnico que continuará el mantenimiento.',
        'warning'
      );
      return;
    }

    const endTimeIso = new Date().toISOString();
    const statusValue =
      mode === 'partial'
        ? 'Parcial'
        : mode === 'pending_delivery'
        ? 'Pendiente de entrega'
        : 'Finalizado';

    const answersArray = Object.keys(answers).map((qId) => ({
      question_id: parseInt(qId, 10),
      answer_text: answers[qId].text || '',
      selected_options: answers[qId].optionIds || [],
      photos: (answers[qId].photos || []).map((p) => p.preview),
      answered_at: questionTimings[qId]?.answered_at || endTimeIso,
      duration_seconds: questionTimings[qId]?.duration_seconds || 0,
    }));

    const cleanRecommendations = recommendations.filter(
      (r) => r.description && r.description.trim() !== ''
    );

    const isSpecial = maintenanceType === 'Emergencia' || maintenanceType === 'Correctivo';
    const effectiveObservations = isSpecial
      ? `Novedad: ${answers[9001]?.text || ''} | Procedimiento: ${answers[9002]?.text || ''} | Subsanada: ${
          answers[9003]?.optionIds?.[0] || 'SI'
        }`
      : generalObservations.trim();

    const payload = {
      project_id: id,
      technician_id: existingReport?.technician_id || userId,
      start_time: startTime,
      end_time: mode === 'partial' ? null : endTimeIso,
      status: statusValue,
      technician_signature:
        existingReport && existingReport.technician_signature && reportIdParam
          ? existingReport.technician_signature
          : execSig,
      continued_by_technician_id:
        mode === 'partial'
          ? continuedByTechnicianId
          : reportIdParam
          ? userId
          : existingReport?.continued_by_technician_id || null,
      continued_by_signature: reportIdParam ? execSig : null,
      partial_close_observations: partialCloseObservations.trim() || null,
      delivery_technician_id: mode === 'final_deliver' ? userId : null,
      delivery_technician_signature: mode === 'final_deliver' ? delivSig || execSig : null,
      customer_signature: mode === 'final_deliver' ? custSig : null,
      customer_name: mode === 'final_deliver' ? customerName.trim() : null,
      maintenance_type: maintenanceType,
      recommendations: cleanRecommendations,
      activities: selectedActivities,
      general_observations: effectiveObservations,
      answers: answersArray,
    };

    // Si no hay conexión a internet, guardar en cola Offline (localStorage)
    if (!navigator.onLine) {
      saveToOfflineQueue({
        endpoint: reportIdParam
          ? `/continueMaintenanceReport/${reportIdParam}`
          : '/saveMaintenanceReport',
        method: reportIdParam ? 'PUT' : 'POST',
        payload,
      });
      setShowSignatureFlow(false);
      await Swal.fire({
        title: 'Guardado en Modo Offline',
        text: 'No hay conexión a internet. El reporte se guardó de forma segura en este dispositivo (localStorage) y se sincronizará al recuperar la conexión.',
        icon: 'info',
        confirmButtonText: 'Entendido',
      });
      navigate('/mantenimiento/clientes');
      return;
    }

    try {
      setLoading(true);
      let resSave;
      if (reportIdParam) {
        resSave = await axios.put(`/continueMaintenanceReport/${reportIdParam}`, payload);
      } else {
        resSave = await axios.post('/saveMaintenanceReport', payload);
      }

      const savedReport = resSave.data?.data;
      const maintenanceReportId = savedReport?.id || reportIdParam || id;

      // Limpiar borrador local tras guardado exitoso
      localStorage.removeItem(draftStorageKey);

      const principalEmail =
        equipo?.customerData?.contacto_principal?.correo ||
        equipo?.customerData?.contactos?.find(
          (c) => String(c.contactoPrincipal) === '1' || c.contactoPrincipal === true
        )?.correo ||
        equipo?.customerData?.contactos?.[0]?.correo ||
        equipo?.customerData?.correo ||
        equipo?.customerData?.email ||
        '';

      const continuedTechObj = techniciansList.find(
        (t) => String(t.id) === String(payload.continued_by_technician_id)
      );

      // Generar PDF con las 3 firmas y hora inicio / final
      const blob = await pdf(
        <MaintenanceReportPdf
          data={{
            id: maintenanceReportId,
            status: statusValue,
            start_time: startTime,
            end_time: mode === 'partial' ? null : endTimeIso,
            technicianSignature: execSig,
            deliveryTechnicianSignature: mode === 'final_deliver' ? delivSig || execSig : null,
            customerSignature: mode === 'final_deliver' ? custSig : null,
            answers,
            maintenanceType,
            recommendations: cleanRecommendations,
            activities: selectedActivities,
            generalObservations: effectiveObservations,
            partial_close_observations: partialCloseObservations.trim() || null,
          }}
          equipo={{
            ...equipo,
            customerEmail: principalEmail,
            customerData: {
              ...(equipo?.customerData || {}),
              correo: principalEmail,
              email: principalEmail,
            },
          }}
          group={questionGroup}
          technicianName={existingReport?.technicianData?.name || userName}
          continuedByTechnicianName={
            reportIdParam ? userName : continuedTechObj?.name || ''
          }
          deliveryTechnicianName={
            mode === 'final_deliver' ? userName : 'Pendiente de entrega'
          }
          customerName={
            mode === 'final_deliver' ? customerName.trim() : 'Pendiente de entrega'
          }
          customerEmail={principalEmail}
          maintenanceType={maintenanceType}
          recommendations={cleanRecommendations}
          activities={selectedActivities}
          backendUrl={backendUrl}
        />
      ).toBlob();

      try {
        const pdfBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });

        if (maintenanceReportId) {
          await axios.put(`/updateMaintenanceReportPdf/${maintenanceReportId}`, {
            pdf_base64: pdfBase64,
          });
        }
      } catch (pdfUploadErr) {
        console.warn('No se pudo actualizar el archivo PDF en el servidor:', pdfUploadErr);
      }

      if (mode !== 'partial') {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const rawCliente =
          equipo?.customerName || equipo?.customerData?.nombre || customerName.trim() || 'Cliente';
        const rawEquipo = equipo?.nombre || equipo?.elevatorTypeName || 'Equipo';
        const rawTipo = maintenanceType || 'Preventivo';
        const cleanCliente = String(rawCliente).trim().replace(/[/\\?%*:|"<>]/g, '_');
        const cleanEquipo = String(rawEquipo).trim().replace(/[/\\?%*:|"<>]/g, '_');
        const cleanTipo = String(rawTipo).trim().replace(/[/\\?%*:|"<>]/g, '_');
        link.download = `${maintenanceReportId}-${cleanCliente}-${cleanEquipo}-${cleanTipo}.pdf`;
        link.click();
      }

      setShowSignatureFlow(false);

      const titleMap = {
        partial: '¡Cierre Parcial Guardado!',
        pending_delivery: '¡Mantenimiento Pendiente de Entrega!',
        final_deliver: '¡Mantenimiento Finalizado y Entregado!',
      };
      const textMap = {
        partial: `El reporte N° ${maintenanceReportId} quedó en estado Parcial y enlazado al técnico seleccionado.`,
        pending_delivery: `El reporte N° ${maintenanceReportId} cerró su ejecución y quedó Pendiente de Entrega al cliente.`,
        final_deliver: `El reporte N° ${maintenanceReportId} se ha guardado con las 3 firmas y el PDF ha sido generado.`,
      };

      await Swal.fire({
        title: titleMap[mode],
        text: textMap[mode],
        icon: 'success',
        confirmButtonText: 'Aceptar',
      });

      navigate('/mantenimiento/clientes');
    } catch (error) {
      console.error('Error saving maintenance report:', error);
      if (!navigator.onLine || error.message === 'Network Error') {
        saveToOfflineQueue({
          endpoint: reportIdParam
            ? `/continueMaintenanceReport/${reportIdParam}`
            : '/saveMaintenanceReport',
          method: reportIdParam ? 'PUT' : 'POST',
          payload,
        });
        setShowSignatureFlow(false);
        await Swal.fire({
          title: 'Sin Conexión — Guardado en LocalStorage',
          text: 'Se perdió la conexión a internet. El reporte fue guardado localmente y podrá sincronizarse al reconectar.',
          icon: 'warning',
        });
        navigate('/mantenimiento/clientes');
        return;
      }
      Swal.fire('Error', 'No se pudo guardar el reporte de mantenimiento', 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderRecommendationsSection = () => {
    if (!showRecommendations && recommendations.length === 0) {
      return (
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            setShowRecommendations(true);
            addRecommendation();
          }}
          startIcon={<PlusIcon className="h-5 w-5" />}
          sx={{
            py: 1.8,
            borderRadius: 3,
            borderStyle: 'dashed',
            borderWidth: 2,
            borderColor: '#94a3b8',
            bgcolor: '#ffffff',
            color: '#1e3a8a',
            fontWeight: '700',
            textTransform: 'none',
            fontSize: '0.9rem',
          }}
        >
          + Agregar Repuestos / Trabajos Recomendados (Opcional)
        </Button>
      );
    }

    return (
      <Card sx={{ borderRadius: 4, border: '1px solid #cbd5e1', overflow: 'hidden' }}>
        <Box
          sx={{
            bgcolor: '#1e3a8a',
            px: 2,
            py: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="subtitle2" fontWeight="800" sx={{ color: '#ffffff' }}>
            REPUESTOS / TRABAJOS RECOMENDADOS (OPCIONAL)
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              onClick={addRecommendation}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: '#fff',
                textTransform: 'none',
                fontWeight: '700',
                borderRadius: 2,
              }}
            >
              + Agregar Fila
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setShowRecommendations(false);
                setRecommendations([]);
              }}
              sx={{ color: 'rgba(255,255,255,0.8)', textTransform: 'none', fontWeight: '700' }}
            >
              Ocultar
            </Button>
          </Stack>
        </Box>
        <CardContent sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            {recommendations.map((rec, rIdx) => (
              <Paper
                key={rIdx}
                variant="outlined"
                sx={{ p: 1.5, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}
              >
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={2} sm={0.8}>
                    <Typography variant="caption" fontWeight="800" color="text.secondary" display="block">
                      Nº
                    </Typography>
                    <Typography variant="subtitle2" fontWeight="800" color="primary">
                      {rIdx + 1}
                    </Typography>
                  </Grid>
                  <Grid item xs={10} sm={4.2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Descripción del repuesto o trabajo"
                      placeholder="Ej: Cambio de relé de seguridad"
                      value={rec.description}
                      onChange={(e) => updateRecommendation(rIdx, 'description', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={4} sm={1.8}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Cant."
                      placeholder="1"
                      value={rec.quantity}
                      onChange={(e) => updateRecommendation(rIdx, 'quantity', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={8} sm={2.2}>
                    <TextField
                      fullWidth
                      select
                      size="small"
                      label="Prioridad"
                      value={rec.priority}
                      onChange={(e) => updateRecommendation(rIdx, 'priority', e.target.value)}
                      SelectProps={{ native: true }}
                    >
                      <option value="Alta">Alta</option>
                      <option value="Media">Media</option>
                      <option value="Baja">Baja</option>
                    </TextField>
                  </Grid>
                  <Grid item xs={10} sm={2.5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Observación"
                      placeholder="Detalle u observación..."
                      value={rec.observation}
                      onChange={(e) => updateRecommendation(rIdx, 'observation', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={2} sm={0.5} sx={{ textAlign: 'center' }}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeRecommendation(rIdx)}
                      title="Eliminar fila"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={10}>
        <CircularProgress />
      </Box>
    );
  }

  const progressPercent =
    totalSteps > 0 ? Math.round(((currentStepIndex + 1) / totalSteps) * 100) : 100;

  return (
    <Box sx={{ p: 2, pb: 10, maxWidth: 900, mx: 'auto' }}>
      {/* Header + Estado Online/Offline + Hora de Inicio */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ChevronLeftIcon className="h-6 w-6" />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <Typography variant="h5" fontWeight="800">
              {reportIdParam
                ? `Continuar Mantenimiento #${reportIdParam}`
                : `Mantenimiento ${maintenanceType}`}
            </Typography>
            <Chip
              icon={<WifiIcon className="h-4 w-4" />}
              label={
                isOnline
                  ? 'En línea (Auto-guardado activo)'
                  : 'Modo Offline (Guardando en dispositivo)'
              }
              size="small"
              color={isOnline ? 'success' : 'warning'}
              variant={isOnline ? 'outlined' : 'filled'}
              sx={{ fontWeight: '700', fontSize: '0.7rem' }}
            />
            <Chip
              icon={<ClockIcon className="h-4 w-4" />}
              label={`Inicio: ${new Date(startTime).toLocaleTimeString('es-CO', {
                hour: '2-digit',
                minute: '2-digit',
              })}`}
              size="small"
              sx={{ bgcolor: '#f1f5f9', fontWeight: '700', fontSize: '0.7rem' }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {equipo?.nombre || equipo?.description} | {equipo?.elevatorTypeName}
          </Typography>
        </Box>
        {preActivitiesCompleted && (
          <Button
            variant="outlined"
            color="warning"
            size="small"
            startIcon={<PauseCircleIcon className="h-4 w-4" />}
            onClick={() => handleOpenCloseFlow('partial')}
            sx={{
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: '800',
              borderColor: '#f59e0b',
              color: '#b45309',
            }}
          >
            Cierre Parcial
          </Button>
        )}
      </Stack>

      {!isOnline && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 3, fontWeight: 600 }}>
          Estás trabajando sin conexión a internet. Todas tus respuestas y fotos se están guardando automáticamente en <b>localStorage</b>.
        </Alert>
      )}

      {/* FASE 0: ACTIVIDADES / ACCIONES PREVIAS ANTES DEL MANTENIMIENTO */}
      {!preActivitiesCompleted ? (
        <Card
          sx={{
            borderRadius: 4,
            border: '1px solid #cbd5e1',
            overflow: 'hidden',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
          }}
        >
          <Box
            sx={{
              bgcolor: '#0f172a',
              px: 2.5,
              py: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <ClipboardDocumentCheckIcon className="h-6 w-6 text-white" />
              <Box>
                <Typography variant="subtitle1" fontWeight="800" sx={{ color: '#ffffff' }}>
                  ACCIONES / ACTIVIDADES PREVIAS ANTES DEL MANTENIMIENTO
                </Typography>
                <Typography variant="caption" sx={{ color: '#cbd5e1' }}>
                  Verifica y marca las acciones previas antes de iniciar el llenado del reporte
                </Typography>
              </Box>
            </Stack>
            {filteredActivities.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleSelectAllActivities}
                sx={{
                  color: '#ffffff',
                  borderColor: 'rgba(255,255,255,0.4)',
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                }}
              >
                Marcar / Desmarcar Todas
              </Button>
            )}
          </Box>

          <CardContent sx={{ p: 3 }}>
            <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2.5 }}>
              Estas actividades corresponden a las <b>acciones previas de preparación y seguridad</b> antes de ejecutar el mantenimiento. Márcalas para continuar con las preguntas del reporte.
            </Alert>

            {filteredActivities.length > 0 ? (
              <Stack spacing={1.2} sx={{ mb: 2.5 }}>
                {filteredActivities.map((act) => {
                  const selectedObj = selectedActivities.find(
                    (s) => String(s.id) === String(act.id)
                  );
                  const isChecked = Boolean(selectedObj);
                  return (
                    <Paper
                      key={act.id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        borderColor: isChecked ? '#2563eb' : '#e2e8f0',
                        bgcolor: isChecked ? '#eff6ff' : '#ffffff',
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isChecked}
                            onChange={() => toggleActivity(act)}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight="700" color="#0f172a">
                              {act.name}
                            </Typography>
                            {act.description && (
                              <Typography variant="caption" color="text.secondary">
                                {act.description}
                              </Typography>
                            )}
                          </Box>
                        }
                        sx={{ width: '100%', m: 0 }}
                      />
                      {isChecked && (
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Observación opcional sobre esta acción previa..."
                          value={selectedObj?.observation || ''}
                          onChange={(e) => updateActivityObservation(act.id, e.target.value)}
                          sx={{ mt: 1, bgcolor: '#fff' }}
                        />
                      )}
                    </Paper>
                  );
                })}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No hay actividades previas preconfiguradas en el catálogo para este tipo de mantenimiento. Puedes registrar una acción previa manual abajo o continuar directamente al cuestionario.
              </Typography>
            )}

            {/* Actividades previas adicionales agregadas manualmente */}
            {selectedActivities
              .filter((s) => String(s.id).startsWith('custom-'))
              .map((customAct) => (
                <Paper
                  key={customAct.id}
                  variant="outlined"
                  sx={{
                    p: 1.2,
                    mb: 1,
                    borderRadius: 2.5,
                    borderColor: '#2563eb',
                    bgcolor: '#eff6ff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body2" fontWeight="700" color="#0f172a">
                    ✓ {customAct.name}
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => toggleActivity(customAct)}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </IconButton>
                </Paper>
              ))}

            <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Agregar otra acción previa (opcional)"
                placeholder="Ej: Verificación de bloqueo de seguridad y señalización"
                value={customPreActivity}
                onChange={(e) => setCustomPreActivity(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCustomPreActivity();
                  }
                }}
              />
              <Button
                variant="outlined"
                onClick={handleAddCustomPreActivity}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                + Añadir
              </Button>
            </Stack>

            <Button
              fullWidth
              variant="contained"
              size="large"
              endIcon={<ChevronRightIcon className="h-5 w-5" />}
              onClick={() => {
                setPreActivitiesCompleted(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              sx={{
                borderRadius: 3,
                py: 1.6,
                fontWeight: '800',
                textTransform: 'none',
                bgcolor: '#1e3a8a',
                '&:hover': { bgcolor: '#1e40af' },
              }}
            >
              Confirmar Acciones Previas e Iniciar Mantenimiento ({selectedActivities.length} marcadas)
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Barra de Navegación por Pregunta (Flechas Izquierda / Derecha) */}
          <Card
            sx={{
              mb: 2.5,
              borderRadius: 3,
              border: '1px solid #cbd5e1',
              bgcolor: '#ffffff',
              boxShadow: '0 2px 8px rgba(15, 23, 42, 0.05)',
            }}
          >
            <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <IconButton
                  onClick={handlePrevStep}
                  sx={{
                    bgcolor: '#eff6ff',
                    color: '#1d4ed8',
                    border: '1px solid #bfdbfe',
                  }}
                  title={
                    currentStepIndex === 0
                      ? 'Volver a Acciones Previas'
                      : 'Pregunta anterior'
                  }
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </IconButton>

                <Box textAlign="center">
                  <Typography variant="subtitle2" fontWeight="800" color="#0f172a">
                    {isFinalSummaryStep
                      ? `Paso Final: Repuestos, Observaciones y Cierre (${totalSteps} de ${totalSteps})`
                      : `Pregunta ${currentStepIndex + 1} de ${activeQuestionsList.length}`}
                  </Typography>
                  {!isFinalSummaryStep && currentQuestion?.group_name && (
                    <Chip
                      label={currentQuestion.group_name}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mt: 0.3, height: 20, fontSize: '0.68rem', fontWeight: '800' }}
                    />
                  )}
                </Box>

                <IconButton
                  onClick={handleNextStep}
                  disabled={currentStepIndex >= totalSteps - 1}
                  sx={{
                    bgcolor: currentStepIndex >= totalSteps - 1 ? '#f1f5f9' : '#eff6ff',
                    color: currentStepIndex >= totalSteps - 1 ? '#94a3b8' : '#1d4ed8',
                    border: '1px solid #bfdbfe',
                  }}
                  title="Siguiente pregunta"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </IconButton>
              </Stack>

              <LinearProgress
                variant="determinate"
                value={progressPercent}
                sx={{ height: 7, borderRadius: 4, bgcolor: '#e2e8f0' }}
              />

              {/* Selector rápido de Acciones Previas + número de pregunta + Cierre */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.7,
                  overflowX: 'auto',
                  py: 1,
                  mt: 0.5,
                  alignItems: 'center',
                }}
              >
                <Chip
                  label={`Acciones Previas (${selectedActivities.length})`}
                  size="small"
                  onClick={() => setPreActivitiesCompleted(false)}
                  sx={{
                    fontWeight: '800',
                    cursor: 'pointer',
                    bgcolor: '#dcfce7',
                    color: '#166534',
                    flexShrink: 0,
                  }}
                />
                {activeQuestionsList.map((q, idx) => {
                  const done = isQuestionAnswered(q);
                  const isCurrent = idx === currentStepIndex;
                  return (
                    <Box
                      key={q.id || idx}
                      onClick={() => setCurrentStepIndex(idx)}
                      sx={{
                        minWidth: 28,
                        height: 28,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: '800',
                        cursor: 'pointer',
                        flexShrink: 0,
                        border: isCurrent ? '2px solid #1e3a8a' : '1px solid #cbd5e1',
                        bgcolor: isCurrent
                          ? '#1e3a8a'
                          : done
                          ? '#dcfce7'
                          : '#f8fafc',
                        color: isCurrent
                          ? '#ffffff'
                          : done
                          ? '#166534'
                          : '#64748b',
                      }}
                    >
                      {idx + 1}
                    </Box>
                  );
                })}
                <Chip
                  label="Cierre"
                  size="small"
                  onClick={() => setCurrentStepIndex(activeQuestionsList.length)}
                  sx={{
                    fontWeight: '800',
                    cursor: 'pointer',
                    bgcolor: isFinalSummaryStep ? '#1e3a8a' : '#f1f5f9',
                    color: isFinalSummaryStep ? '#ffffff' : '#334155',
                    flexShrink: 0,
                  }}
                />
              </Box>
            </Box>
          </Card>

          {/* CONTENIDO DE LA PREGUNTA ACTUAL (1 A LA VEZ) O PASO FINAL */}
          {!isFinalSummaryStep ? (
            <Box>
              {isSpecialType ? (
                /* MODO EMERGENCIA / CORRECTIVO: 1 PASO A LA VEZ */
                <Card sx={{ borderRadius: 4, border: '1px solid #cbd5e1', overflow: 'hidden' }}>
                  <Box sx={{ bgcolor: '#1e3a8a', px: 2.5, py: 1.8 }}>
                    <Typography variant="subtitle1" fontWeight="800" sx={{ color: '#ffffff' }}>
                      {currentQuestion?.title}
                    </Typography>
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    {currentQuestion?.id === 9001 && (
                      <>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          required
                          label="Detalle de la novedad o falla reportada *"
                          placeholder="Describe detalladamente la falla presentada, síntomas reportados, estado en que se encontró el equipo..."
                          value={answers[9001]?.text || ''}
                          onChange={(e) => handleAnswerChange(9001, e.target.value, 'texto')}
                        />
                        <Box sx={{ mt: 2.5, pt: 1.5, borderTop: '1px dashed #e2e8f0' }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 1,
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center">
                              <CameraIcon className="h-5 w-5 text-gray-500" />
                              <Typography variant="caption" fontWeight="700">
                                FOTOS DE LA NOVEDAD ({answers[9001]?.photos?.length || 0})
                              </Typography>
                            </Stack>
                            <Button
                              size="small"
                              component="label"
                              variant="contained"
                              startIcon={<CameraIcon className="h-4 w-4" />}
                              sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                bgcolor: '#f1f5f9',
                                color: '#1e293b',
                              }}
                            >
                              Cargar Fotos
                              <input
                                type="file"
                                hidden
                                multiple
                                accept="image/*"
                                capture="environment"
                                onChange={(e) => handleFileChange(9001, e)}
                              />
                            </Button>
                          </Box>
                          {answers[9001]?.photos?.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pt: 1, pb: 0.5 }}>
                              {answers[9001].photos.map((photo, pIdx) => (
                                <Box
                                  key={pIdx}
                                  sx={{
                                    position: 'relative',
                                    flexShrink: 0,
                                    width: 80,
                                    height: 80,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: '2px solid #e2e8f0',
                                  }}
                                >
                                  <img
                                    src={getImageUrl(photo.preview)}
                                    alt="Preview"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() => removePhoto(9001, pIdx)}
                                    sx={{
                                      position: 'absolute',
                                      top: 2,
                                      right: 2,
                                      bgcolor: 'rgba(255,255,255,0.9)',
                                      p: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 14,
                                        height: 14,
                                        bgcolor: '#ef4444',
                                        borderRadius: '50%',
                                        color: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 10,
                                      }}
                                    >
                                      ×
                                    </Box>
                                  </IconButton>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </>
                    )}

                    {currentQuestion?.id === 9002 && (
                      <>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          required
                          label="Detalle del procedimiento o labor ejecutada *"
                          placeholder="Describe las maniobras, ajustes, pruebas o reparaciones realizadas..."
                          value={answers[9002]?.text || ''}
                          onChange={(e) => handleAnswerChange(9002, e.target.value, 'texto')}
                        />
                        <Box sx={{ mt: 2.5, pt: 1.5, borderTop: '1px dashed #e2e8f0' }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 1,
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center">
                              <CameraIcon className="h-5 w-5 text-gray-500" />
                              <Typography variant="caption" fontWeight="700">
                                FOTOS DEL PROCEDIMIENTO ({answers[9002]?.photos?.length || 0})
                              </Typography>
                            </Stack>
                            <Button
                              size="small"
                              component="label"
                              variant="contained"
                              startIcon={<CameraIcon className="h-4 w-4" />}
                              sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                bgcolor: '#f1f5f9',
                                color: '#1e293b',
                              }}
                            >
                              Cargar Fotos
                              <input
                                type="file"
                                hidden
                                multiple
                                accept="image/*"
                                capture="environment"
                                onChange={(e) => handleFileChange(9002, e)}
                              />
                            </Button>
                          </Box>
                          {answers[9002]?.photos?.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pt: 1, pb: 0.5 }}>
                              {answers[9002].photos.map((photo, pIdx) => (
                                <Box
                                  key={pIdx}
                                  sx={{
                                    position: 'relative',
                                    flexShrink: 0,
                                    width: 80,
                                    height: 80,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: '2px solid #e2e8f0',
                                  }}
                                >
                                  <img
                                    src={getImageUrl(photo.preview)}
                                    alt="Preview"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() => removePhoto(9002, pIdx)}
                                    sx={{
                                      position: 'absolute',
                                      top: 2,
                                      right: 2,
                                      bgcolor: 'rgba(255,255,255,0.9)',
                                      p: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 14,
                                        height: 14,
                                        bgcolor: '#ef4444',
                                        borderRadius: '50%',
                                        color: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 10,
                                      }}
                                    >
                                      ×
                                    </Box>
                                  </IconButton>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </>
                    )}

                    {currentQuestion?.id === 9003 && (
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Paper
                            elevation={0}
                            onClick={() => handleAnswerChange(9003, 'SI', 'unica')}
                            sx={{
                              p: 2.5,
                              borderRadius: 3,
                              border: `2px solid ${
                                (answers[9003]?.optionIds?.[0] || 'SI') === 'SI'
                                  ? '#10b981'
                                  : '#e2e8f0'
                              }`,
                              bgcolor:
                                (answers[9003]?.optionIds?.[0] || 'SI') === 'SI'
                                  ? '#dcfce7'
                                  : '#ffffff',
                              cursor: 'pointer',
                              textAlign: 'center',
                            }}
                          >
                            <Typography variant="h6" fontWeight="800" sx={{ color: '#15803d' }}>
                              SI
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#166534', fontWeight: '600' }}>
                              Falla completamente corregida
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6}>
                          <Paper
                            elevation={0}
                            onClick={() => handleAnswerChange(9003, 'NO', 'unica')}
                            sx={{
                              p: 2.5,
                              borderRadius: 3,
                              border: `2px solid ${
                                answers[9003]?.optionIds?.[0] === 'NO' ? '#ef4444' : '#e2e8f0'
                              }`,
                              bgcolor:
                                answers[9003]?.optionIds?.[0] === 'NO' ? '#fee2e2' : '#ffffff',
                              cursor: 'pointer',
                              textAlign: 'center',
                            }}
                          >
                            <Typography variant="h6" fontWeight="800" sx={{ color: '#b91c1c' }}>
                              NO
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#991b1b', fontWeight: '600' }}>
                              Pendiente por repuestos o intervención mayor
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    )}
                  </CardContent>
                </Card>
              ) : (
                /* MODO PREVENTIVO (CHECKLIST): 1 PREGUNTA A LA VEZ */
                (() => {
                  if (!currentQuestion) {
                    return (
                      <Card sx={{ borderRadius: 4, border: '1px dashed #cbd5e1' }}>
                        <CardContent>
                          <Typography variant="body2" color="text.secondary" textAlign="center">
                            Este equipo no tiene preguntas asignadas. Asígnalas desde el módulo de Equipos.
                          </Typography>
                        </CardContent>
                      </Card>
                    );
                  }
                  const ans = answers[currentQuestion.id];
                  const selectedOptions =
                    currentQuestion.options?.filter((opt) =>
                      ans?.optionIds?.includes(opt.id.toString())
                    ) || [];
                  const requiresEvidenceByOption = selectedOptions.some((opt) => opt.requires_photo);
                  const requiresJustificationByOption = selectedOptions.some(
                    (opt) => opt.requires_justification || opt.requires_justification === 1
                  );

                  return (
                    <Card
                      key={currentQuestion.id}
                      sx={{
                        borderRadius: 4,
                        border: '1px solid #cbd5e1',
                        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
                      }}
                    >
                      <Box
                        sx={{
                          bgcolor: '#1e3a8a',
                          px: 2.5,
                          py: 1.5,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight="800"
                          sx={{ color: '#bfdbfe', textTransform: 'uppercase' }}
                        >
                          {currentQuestion.group_name || 'Inspección General'}
                        </Typography>
                        <Chip
                          label={isQuestionAnswered(currentQuestion) ? 'Respondida' : 'Pendiente'}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.68rem',
                            fontWeight: '800',
                            bgcolor: isQuestionAnswered(currentQuestion)
                              ? '#dcfce7'
                              : 'rgba(255,255,255,0.2)',
                            color: isQuestionAnswered(currentQuestion) ? '#166534' : '#ffffff',
                          }}
                        />
                      </Box>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" fontWeight="800" sx={{ mb: 2.5, color: '#0f172a' }}>
                          {currentStepIndex + 1}. {currentQuestion.text}
                        </Typography>

                        {currentQuestion.type === 'unica' && (
                          <RadioGroup
                            value={ans?.optionIds?.[0] || ''}
                            onChange={(e) =>
                              handleAnswerChange(currentQuestion.id, e.target.value, 'unica')
                            }
                          >
                            <Stack spacing={1}>
                              {currentQuestion.options?.map((opt) => {
                                const selected = ans?.optionIds?.[0] === opt.id.toString();
                                return (
                                  <Paper
                                    key={opt.id}
                                    variant="outlined"
                                    onClick={() =>
                                      handleAnswerChange(
                                        currentQuestion.id,
                                        opt.id.toString(),
                                        'unica'
                                      )
                                    }
                                    sx={{
                                      px: 2,
                                      py: 1,
                                      borderRadius: 2.5,
                                      cursor: 'pointer',
                                      borderColor: selected ? '#2563eb' : '#e2e8f0',
                                      bgcolor: selected ? '#eff6ff' : '#ffffff',
                                    }}
                                  >
                                    <FormControlLabel
                                      value={opt.id.toString()}
                                      control={<Radio />}
                                      label={
                                        <Typography fontWeight={selected ? 700 : 500}>
                                          {opt.text}
                                        </Typography>
                                      }
                                      sx={{ width: '100%', m: 0 }}
                                    />
                                  </Paper>
                                );
                              })}
                            </Stack>
                          </RadioGroup>
                        )}

                        {currentQuestion.type === 'multiple' && (
                          <FormGroup>
                            <Stack spacing={1}>
                              {currentQuestion.options?.map((opt) => {
                                const checked =
                                  ans?.optionIds?.includes(opt.id.toString()) || false;
                                return (
                                  <Paper
                                    key={opt.id}
                                    variant="outlined"
                                    sx={{
                                      px: 2,
                                      py: 1,
                                      borderRadius: 2.5,
                                      borderColor: checked ? '#2563eb' : '#e2e8f0',
                                      bgcolor: checked ? '#eff6ff' : '#ffffff',
                                    }}
                                  >
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={checked}
                                          onChange={() =>
                                            handleAnswerChange(
                                              currentQuestion.id,
                                              opt.id.toString(),
                                              'multiple'
                                            )
                                          }
                                        />
                                      }
                                      label={opt.text}
                                    />
                                  </Paper>
                                );
                              })}
                            </Stack>
                          </FormGroup>
                        )}

                        {currentQuestion.type === 'abierta' && (
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Escribe tu respuesta..."
                            value={ans?.text || ''}
                            onChange={(e) =>
                              handleAnswerChange(currentQuestion.id, e.target.value, 'texto')
                            }
                          />
                        )}

                        {currentQuestion.type !== 'abierta' && requiresJustificationByOption && (
                          <Box sx={{ mt: 2.5 }}>
                            <TextField
                              fullWidth
                              multiline
                              size="small"
                              rows={2}
                              label="Justificación / Observación requerida *"
                              placeholder="Escribe la justificación del hallazgo aquí..."
                              value={ans?.text || ''}
                              onChange={(e) =>
                                handleAnswerChange(currentQuestion.id, e.target.value, 'texto')
                              }
                              required
                            />
                          </Box>
                        )}

                        {(currentQuestion.type === 'fotos' ||
                          currentQuestion.min_photos > 0 ||
                          requiresEvidenceByOption) && (
                          <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px dashed #cbd5e1' }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 1,
                              }}
                            >
                              <Stack direction="row" spacing={1} alignItems="center">
                                <CameraIcon className="h-5 w-5 text-gray-500" />
                                <Typography variant="caption" fontWeight="700">
                                  FOTOS DE EVIDENCIA ({ans?.photos?.length || 0})
                                </Typography>
                              </Stack>
                              <Stack direction="row" spacing={1}>
                                {requiresEvidenceByOption && (
                                  <Chip
                                    label="Foto requerida *"
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.65rem',
                                      bgcolor:
                                        (ans?.photos?.length || 0) > 0 ? '#dcfce7' : '#fee2e2',
                                      color:
                                        (ans?.photos?.length || 0) > 0 ? '#166534' : '#991b1b',
                                      fontWeight: '700',
                                    }}
                                  />
                                )}
                                {currentQuestion.min_photos > 0 && (
                                  <Chip
                                    label={`Mín: ${currentQuestion.min_photos}`}
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.65rem',
                                      bgcolor:
                                        (ans?.photos?.length || 0) >= currentQuestion.min_photos
                                          ? '#dcfce7'
                                          : '#fee2e2',
                                    }}
                                  />
                                )}
                                {currentQuestion.max_photos > 0 && (
                                  <Chip
                                    label={`Máx: ${currentQuestion.max_photos}`}
                                    size="small"
                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                  />
                                )}
                              </Stack>
                              {(!currentQuestion.max_photos ||
                                (ans?.photos?.length || 0) < currentQuestion.max_photos) && (
                                <Button
                                  size="small"
                                  component="label"
                                  variant="contained"
                                  startIcon={<CameraIcon className="h-4 w-4" />}
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    bgcolor: '#f1f5f9',
                                    color: '#1e293b',
                                  }}
                                >
                                  Cargar Foto
                                  <input
                                    type="file"
                                    hidden
                                    multiple
                                    accept="image/*"
                                    capture="environment"
                                    onChange={(e) => handleFileChange(currentQuestion.id, e)}
                                  />
                                </Button>
                              )}
                            </Box>
                            {ans?.photos?.length > 0 && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 1.5,
                                  overflowX: 'auto',
                                  pt: 1,
                                  pb: 0.5,
                                }}
                              >
                                {ans.photos.map((photo, pIdx) => (
                                  <Box
                                    key={pIdx}
                                    sx={{
                                      position: 'relative',
                                      flexShrink: 0,
                                      width: 80,
                                      height: 80,
                                      borderRadius: 2,
                                      overflow: 'hidden',
                                      border: '2px solid #e2e8f0',
                                    }}
                                  >
                                    <img
                                      src={getImageUrl(photo.preview)}
                                      alt="Preview"
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                      }}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={() => removePhoto(currentQuestion.id, pIdx)}
                                      sx={{
                                        position: 'absolute',
                                        top: 2,
                                        right: 2,
                                        bgcolor: 'rgba(255,255,255,0.9)',
                                        p: 0.5,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 14,
                                          height: 14,
                                          bgcolor: '#ef4444',
                                          borderRadius: '50%',
                                          color: 'white',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: 10,
                                        }}
                                      >
                                        ×
                                      </Box>
                                    </IconButton>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  );
                })()
              )}

              {/* Botones Inferiores de Flechas de Navegación (Anterior / Siguiente) */}
              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={handlePrevStep}
                  startIcon={<ChevronLeftIcon className="h-5 w-5" />}
                  sx={{
                    borderRadius: 3,
                    py: 1.5,
                    fontWeight: '800',
                    textTransform: 'none',
                    borderColor: '#cbd5e1',
                    color: '#334155',
                  }}
                >
                  {currentStepIndex === 0 ? 'Acciones Previas' : 'Anterior'}
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleNextStep}
                  endIcon={<ChevronRightIcon className="h-5 w-5" />}
                  sx={{
                    borderRadius: 3,
                    py: 1.5,
                    fontWeight: '800',
                    textTransform: 'none',
                    bgcolor: '#1e3a8a',
                    '&:hover': { bgcolor: '#1e40af' },
                  }}
                >
                  {currentStepIndex === activeQuestionsList.length - 1
                    ? 'Ir a Cierre y Firmas'
                    : 'Siguiente Pregunta'}
                </Button>
              </Stack>
            </Box>
          ) : (
            /* PASO FINAL: RESUMEN DE ACCIONES PREVIAS, REPUESTOS RECOMENDADOS, OBSERVACIONES Y OPCIONES DE CIERRE */
            <Stack spacing={3}>
              <Card sx={{ borderRadius: 4, border: '1px solid #cbd5e1', overflow: 'hidden' }}>
                <Box
                  sx={{
                    bgcolor: '#0f172a',
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ClipboardDocumentCheckIcon className="h-5 w-5 text-white" />
                    <Typography variant="subtitle2" fontWeight="800" sx={{ color: '#ffffff' }}>
                      ACCIONES PREVIAS REALIZADAS ({selectedActivities.length})
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    onClick={() => setPreActivitiesCompleted(false)}
                    sx={{ color: '#93c5fd', textTransform: 'none', fontWeight: 700 }}
                  >
                    Editar acciones previas
                  </Button>
                </Box>
                <CardContent sx={{ p: 2 }}>
                  {selectedActivities.length > 0 ? (
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {selectedActivities.map((act) => (
                        <Chip
                          key={act.id}
                          label={
                            act.observation
                              ? `${act.name} (${act.observation})`
                              : act.name
                          }
                          color="primary"
                          variant="outlined"
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No se marcaron acciones previas para este mantenimiento.
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {renderRecommendationsSection()}

              {!isSpecialType && (
                <Card sx={{ borderRadius: 4, border: '1px solid #cbd5e1', overflow: 'hidden' }}>
                  <Box sx={{ bgcolor: '#475569', px: 2, py: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight="800" sx={{ color: '#ffffff' }}>
                      OBSERVACIONES GENERALES
                    </Typography>
                  </Box>
                  <CardContent sx={{ p: 2 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Escribe observaciones generales del mantenimiento..."
                      value={generalObservations}
                      onChange={(e) => setGeneralObservations(e.target.value)}
                    />
                  </CardContent>
                </Card>
              )}

              <Card sx={{ borderRadius: 4, border: '2px solid #e2e8f0', p: 2.5, bgcolor: '#f8fafc' }}>
                <Typography variant="subtitle1" fontWeight="800" color="#0f172a" gutterBottom>
                  Opciones de Cierre del Mantenimiento
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Puedes dejar el mantenimiento <b>Pendiente de Entrega</b> (si se entregará al cliente después), realizar la <b>Entrega Inmediata con las 3 firmas</b>, o hacer un <b>Cierre Parcial</b> para que otro técnico lo continúe.
                </Typography>

                <Stack spacing={1.5}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<CheckCircleIcon className="h-5 w-5" />}
                    onClick={() => handleOpenCloseFlow('final_deliver')}
                    sx={{
                      borderRadius: 3,
                      py: 1.5,
                      fontWeight: '800',
                      textTransform: 'none',
                      bgcolor: '#16a34a',
                      '&:hover': { bgcolor: '#15803d' },
                    }}
                  >
                    Finalizar y Entregar Ahora (3 Firmas: Ejecutó, Entregó y Recibe)
                  </Button>

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={() => handleOpenCloseFlow('pending_delivery')}
                    sx={{
                      borderRadius: 3,
                      py: 1.5,
                      fontWeight: '800',
                      textTransform: 'none',
                      bgcolor: '#1e3a8a',
                      '&:hover': { bgcolor: '#1e40af' },
                    }}
                  >
                    Cerrar Ejecución — Dejar Pendiente de Entrega (Solo Firma Quien Ejecutó)
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    startIcon={<PauseCircleIcon className="h-5 w-5" />}
                    onClick={() => handleOpenCloseFlow('partial')}
                    sx={{
                      borderRadius: 3,
                      py: 1.3,
                      fontWeight: '800',
                      textTransform: 'none',
                      borderColor: '#f59e0b',
                      color: '#b45309',
                    }}
                  >
                    Cierre Parcial y Enlazar Quién lo Continúa
                  </Button>

                  <Button
                    fullWidth
                    variant="text"
                    startIcon={<ChevronLeftIcon className="h-4 w-4" />}
                    onClick={handlePrevStep}
                    sx={{ textTransform: 'none', fontWeight: '700', color: '#475569' }}
                  >
                    Volver a las preguntas
                  </Button>
                </Stack>
              </Card>
            </Stack>
          )}
        </>
      )}

      {/* DIALOG DE FIRMAS Y CIERRE (PARCIAL / PENDIENTE ENTREGA / FINAL 3 FIRMAS) */}
      <Dialog
        open={showSignatureFlow}
        fullWidth
        maxWidth="sm"
        onClose={() => setShowSignatureFlow(false)}
      >
        <DialogTitle component="div" sx={{ pb: 0 }}>
          <Typography variant="h6" fontWeight="800">
            {closeMode === 'partial'
              ? 'Cierre Parcial de Mantenimiento'
              : closeMode === 'pending_delivery'
              ? 'Cerrar Ejecución (Pendiente de Entrega)'
              : 'Finalizar y Entregar Mantenimiento (3 Firmas)'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {closeMode === 'final_deliver' && (
            <Box sx={{ width: '100%', my: 2 }}>
              <Stepper
                activeStep={
                  signatureStep === 'executor' ? 0 : signatureStep === 'delivery' ? 1 : 2
                }
                alternativeLabel
              >
                <Step>
                  <StepLabel>1. Quien Ejecutó (Técnico)</StepLabel>
                </Step>
                <Step>
                  <StepLabel>2. Quien Entrega (Empresa)</StepLabel>
                </Step>
                <Step>
                  <StepLabel>3. Quien Recibe (Cliente)</StepLabel>
                </Step>
              </Stepper>
            </Box>
          )}

          {closeMode === 'partial' && (
            <Stack spacing={2} sx={{ mt: 2, mb: 2 }}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Selecciona el técnico que continuará este mantenimiento. El avance actual quedará guardado en estado <b>Parcial</b>.
              </Alert>
              <FormControl fullWidth required>
                <InputLabel>Técnico que continúa el mantenimiento *</InputLabel>
                <Select
                  label="Técnico que continúa el mantenimiento *"
                  value={continuedByTechnicianId}
                  onChange={(e) => setContinuedByTechnicianId(e.target.value)}
                >
                  {techniciansList.map((tech) => (
                    <MenuItem key={tech.id} value={tech.id}>
                      {tech.name} ({tech.Rol?.name || 'Técnico'})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Observaciones del cierre parcial"
                placeholder="Indica en qué punto queda el mantenimiento o qué falta por realizar..."
                value={partialCloseObservations}
                onChange={(e) => setPartialCloseObservations(e.target.value)}
              />
            </Stack>
          )}

          <Divider sx={{ my: 2 }} />

          {/* PASO 1 O 2: FIRMA DE TÉCNICO (QUIEN EJECUTÓ O QUIEN ENTREGA) */}
          {(signatureStep === 'executor' || signatureStep === 'delivery') && (
            <Box>
              <Typography variant="subtitle2" fontWeight="800" color="primary" sx={{ mb: 1.5 }}>
                {signatureStep === 'executor'
                  ? 'Firma de Quien Ejecutó el Mantenimiento (Técnico):'
                  : 'Firma de Quien Entrega el Equipo (Técnico / Empresa):'}
              </Typography>

              {signatureStep === 'delivery' && technicianSignature && (
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  size="large"
                  onClick={() => onTechSignatureConfirmed(technicianSignature)}
                  sx={{
                    mb: 2,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontWeight: '800',
                    py: 1.2,
                  }}
                >
                  Usar la misma firma de Quien Ejecutó y Continuar
                </Button>
              )}

              {!isDrawingNewTech && savedSignatures.length > 0 ? (
                <Stack spacing={2}>
                  <Paper
                    sx={{
                      p: 2,
                      border: '2px solid #e2e8f0',
                      borderRadius: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      bgcolor: '#f8fafc',
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        maxWidth: 300,
                        height: 110,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#fff',
                        borderRadius: 2,
                        p: 1,
                        border: '1px solid #e2e8f0',
                        mb: 1,
                      }}
                    >
                      <img
                        src={getImageUrl(savedSignatures[0].signature)}
                        alt="Firma Guardada"
                        style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                      />
                    </Box>
                    <Typography variant="subtitle2" fontWeight="700" color="text.primary">
                      {savedSignatures[0].name || 'Firma de Técnico'}
                    </Typography>
                  </Paper>

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={() => handleSelectSavedSignature(savedSignatures[0])}
                    sx={{
                      borderRadius: 3,
                      textTransform: 'none',
                      fontWeight: '800',
                      py: 1.2,
                      bgcolor: '#3b82f6',
                    }}
                  >
                    Usar Mi Firma Guardada
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PencilIcon className="h-4 w-4" />}
                    onClick={() => setIsDrawingNewTech(true)}
                    sx={{
                      borderRadius: 3,
                      textTransform: 'none',
                      fontWeight: '700',
                      color: '#64748b',
                      borderColor: '#cbd5e1',
                    }}
                  >
                    Dibujar Otra Firma
                  </Button>
                </Stack>
              ) : (
                <SignaturePad
                  key={`sig-${signatureStep}`}
                  title={
                    signatureStep === 'executor'
                      ? 'Dibuja la firma de Quien Ejecutó'
                      : 'Dibuja la firma de Quien Entrega'
                  }
                  onSave={handleSaveTechSignature}
                  onCancel={
                    savedSignatures.length > 0 ? () => setIsDrawingNewTech(false) : null
                  }
                />
              )}
            </Box>
          )}

          {/* PASO 3: FIRMA DE QUIEN RECIBE (CLIENTE) */}
          {signatureStep === 'customer' && (
            <Box>
              <TextField
                fullWidth
                label="Nombre completo de quien recibe (Cliente) *"
                placeholder="Ej: Juan Pérez García"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                required
                sx={{ mb: 2 }}
                inputProps={{ maxLength: 120 }}
              />
              <SignaturePad
                key="cust-sig"
                title="Firma de Quien Recibe (Cliente)"
                onSave={(custSigBase64) =>
                  handleExecuteSubmit({
                    mode: 'final_deliver',
                    execSig: technicianSignature,
                    delivSig: deliveryTechnicianSignature || technicianSignature,
                    custSig: custSigBase64,
                  })
                }
              />
              <Button
                fullWidth
                variant="text"
                onClick={() => setSignatureStep('delivery')}
                sx={{ mt: 1, color: '#64748b', textTransform: 'none', fontWeight: '700' }}
              >
                Volver a firma de quien entrega
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
          <Button
            onClick={() => setShowSignatureFlow(false)}
            sx={{ color: '#64748b', fontWeight: '700' }}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
